import React, { useEffect, useState } from 'react';
import { useMultiplayerDiagnostics } from '../../../diagnostics/useMultiplayerDiagnostics';
import { DIAGNOSTICS_THRESHOLDS } from '../../../../shared/constants/DiagnosticsConfig';
import { PlayerLifecycleLogger, LifecycleSnapshot } from '../../../diagnostics/PlayerLifecycleLogger';

function formatNumber(value: number | null): string {
  return value === null ? 'n/a' : String(Math.round(value));
}

function formatCoord(x: number | null, y: number | null): string {
  if (x === null || y === null) {
    return 'n/a';
  }
  return `(${Math.round(x)}, ${Math.round(y)})`;
}

function formatBool(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return 'n/a';
  }
  return value ? 'yes' : 'no';
}

export const MultiplayerDiagnosticsPanelContent: React.FC = () => {
  const { snapshot, labelsEnabled } = useMultiplayerDiagnostics();
  const [lifecycleEvents, setLifecycleEvents] = useState<LifecycleSnapshot[]>([]);

  useEffect(() => {
    const tick = () => setLifecycleEvents(PlayerLifecycleLogger.getLatest(8));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const schemaRenderDx = Math.abs((snapshot.local.schemaX ?? 0) - (snapshot.local.renderX ?? 0));
  const schemaRenderDy = Math.abs((snapshot.local.schemaY ?? 0) - (snapshot.local.renderY ?? 0));
  const schemaRenderDist = Math.sqrt(schemaRenderDx ** 2 + schemaRenderDy ** 2);

  const healthChecks = [
    { label: 'Schema-Render', ok: schemaRenderDist < DIAGNOSTICS_THRESHOLDS.SCHEMA_RENDER_DIST.OK, warn: schemaRenderDist < DIAGNOSTICS_THRESHOLDS.SCHEMA_RENDER_DIST.WARN, value: `${schemaRenderDist.toFixed(0)}px` },
    { label: 'Connected', ok: snapshot.ready, warn: snapshot.ready, value: snapshot.ready ? 'yes' : 'no' },
  ];
  const allGood = healthChecks.every(c => c.ok);
  const hasError = healthChecks.some(c => !c.warn);
  const healthStatus = allGood ? 'HEALTHY' : hasError ? 'DEGRADED' : 'WARNING';
  const healthColor = allGood ? '#2ecc71' : hasError ? '#e74c3c' : '#f39c12';

  return (
      <div
        data-testid="mp-diag-panel"
        style={{
          height: '100%',
          overflow: 'auto',
          padding: '10px',
          background: 'rgba(4, 8, 16, 0.92)',
          color: '#dfe7ff',
          fontFamily: 'monospace',
          fontSize: '10px',
          lineHeight: 1.5,
        }}
      >
        <div style={metaRowStyle}>
          <span>Captured: {snapshot.capturedAt}</span>
          <span>Ready: {snapshot.ready ? 'yes' : 'no'}</span>
          <span>Labels: {labelsEnabled ? 'on' : 'off'}</span>
        </div>

        <div style={{ ...healthBannerStyle, borderColor: healthColor }}>
          <span style={{ color: healthColor, fontWeight: 700, fontSize: '11px' }}>
            {healthStatus}
          </span>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {healthChecks.map(c => (
              <span key={c.label} style={{ color: c.ok ? '#2ecc71' : c.warn ? '#f39c12' : '#e74c3c' }}>
                {c.label}: {c.value}
              </span>
            ))}
          </div>
        </div>

        <Section title="Local">
          <KV dataTestId="mp-diag-room-id" label="Room ID" value={snapshot.local.roomId || 'n/a'} />
          <KV dataTestId="mp-diag-room-instance-id" label="Room Instance ID" value={snapshot.local.roomInstanceId || 'n/a'} />
          <KV label="Room Created" value={snapshot.local.roomCreatedAt || 'n/a'} />
          <KV label="Session ID" value={snapshot.local.sessionId || 'n/a'} />
          <KV label="Client Instance" value={snapshot.local.clientInstanceId || 'n/a'} />
          <KV label="First Room ID" value={snapshot.local.firstRoomId || 'n/a'} />
          <KV label="First Room Instance" value={snapshot.local.firstRoomInstanceId || 'n/a'} />
          <KV dataTestId="mp-diag-reconnect-count" label="Reconnect Count" value={String(snapshot.local.reconnectCount)} />
          <KV label="Reconnect Attempts" value={String(snapshot.local.reconnectAttempts)} />
          <KV label="Last Disconnect" value={snapshot.local.disconnectReason || 'n/a'} />
          <KV dataTestId="mp-diag-local-schema-pos" label="Schema" value={formatCoord(snapshot.local.schemaX, snapshot.local.schemaY)} />
          <KV dataTestId="mp-diag-local-render-pos" label="Render" value={formatCoord(snapshot.local.renderX, snapshot.local.renderY)} />
          <KV dataTestId="mp-diag-local-grounded" label="Grounded" value={formatBool(snapshot.local.grounded)} />
          <KV label="Velocity X" value={snapshot.local.velocityX !== null ? (Math.round(snapshot.local.velocityX * 10) / 10).toFixed(1) : 'n/a'} />
          <KV dataTestId="mp-diag-local-velocity-y" label="Velocity Y" value={snapshot.local.velocityY !== null ? (Math.round(snapshot.local.velocityY * 10) / 10).toFixed(1) : 'n/a'} />
          <KV dataTestId="mp-diag-reset-detected" label="Reset Detected" value={snapshot.local.resetDetected ? 'yes' : 'no'} />
          <KV label="Room Changed Since Join" value={snapshot.local.roomChangedSinceJoin ? 'yes' : 'no'} />
          <KV label="Room Instance Changed" value={snapshot.local.roomInstanceChangedSinceJoin ? 'yes' : 'no'} />
          <KV label="Room Created Changed" value={snapshot.local.roomCreatedAtChangedSinceJoin ? 'yes' : 'no'} />
        </Section>

        <Section title="Remotes">
          <KV dataTestId="mp-diag-remote-schema-count" label="Schema Count" value={String(snapshot.remoteSchemaCount)} />
          <KV label="Manager Count" value={String(snapshot.remoteManagerCount)} />
          <KV dataTestId="mp-diag-remote-render-count" label="Render Count" value={String(snapshot.remoteRenderCount)} />

          {snapshot.remotes.length === 0 ? (
            <div style={emptyStateStyle}>No remotes observed</div>
          ) : (
            snapshot.remotes.map((remote) => (
              <div
                key={remote.sessionId}
                data-testid={`mp-diag-remote-${remote.sessionSuffix}`}
                style={remoteCardStyle}
              >
                <div style={remoteHeaderStyle}>
                  <strong>{remote.name || 'Remote'}</strong>
                  <span>{remote.sessionSuffix}</span>
                </div>
                <KV
                  dataTestId={`mp-diag-remote-${remote.sessionSuffix}-schema`}
                  label="Schema"
                  value={formatCoord(remote.schemaX, remote.schemaY)}
                />
                <KV
                  dataTestId={`mp-diag-remote-${remote.sessionSuffix}-render`}
                  label="Render"
                  value={formatCoord(remote.renderX, remote.renderY)}
                />
                <KV
                  dataTestId={`mp-diag-remote-${remote.sessionSuffix}-delta`}
                  label="Delta"
                  value={formatCoord(remote.deltaX, remote.deltaY)}
                />
                <KV
                  dataTestId={`mp-diag-remote-${remote.sessionSuffix}-sprite`}
                  label="Sprite Exists"
                  value={remote.spriteExists ? 'yes' : 'no'}
                />
                <KV
                  dataTestId={`mp-diag-remote-${remote.sessionSuffix}-grounded`}
                  label="Grounded"
                  value={formatBool(remote.grounded)}
                />
                <KV
                  dataTestId={`mp-diag-remote-${remote.sessionSuffix}-velocity-y`}
                  label="Velocity Y"
                  value={remote.velocityY !== null ? (Math.round(remote.velocityY * 10) / 10).toFixed(1) : 'n/a'}
                />
                {(remote.health !== null || remote.mana !== null) && (
                  <KV label="HP / MP" value={`${formatNumber(remote.health)}/${formatNumber(remote.maxHealth)} | ${formatNumber(remote.mana)}/${formatNumber(remote.maxMana)}`} />
                )}
                <KV label="Missing Sprite Grace Elapsed" value={remote.missingSpriteGraceElapsed ? 'yes' : 'no'} />
              </div>
            ))
          )}
        </Section>

        <Section title="Events">
          <KV dataTestId="mp-diag-ev-player-teleported" label="playerTeleported" value={String(snapshot.eventCounters.playerTeleported)} />
          <KV label="remoteJoin" value={String(snapshot.eventCounters.remoteJoin)} />
          <KV label="remoteLeave" value={String(snapshot.eventCounters.remoteLeave)} />
          <KV label="reconnect" value={String(snapshot.eventCounters.reconnect)} />
          <KV label="Reconnect Started" value={snapshot.local.reconnectStartedAt || 'n/a'} />
          <KV label="Reconnect Succeeded" value={snapshot.local.reconnectSucceededAt || 'n/a'} />
          <KV label="Reconnect Failed" value={snapshot.local.reconnectFailedAt || 'n/a'} />
        </Section>

        <Section title="Lifecycle">
          {lifecycleEvents.length === 0 ? (
            <div style={emptyStateStyle}>No lifecycle events</div>
          ) : (
            <div style={{ fontSize: '9px', lineHeight: 1.6 }}>
              {lifecycleEvents.map((ev, i) => {
                const local = ev.localPosition
                  ? `(${Math.round(ev.localPosition.x)},${Math.round(ev.localPosition.y)})`
                  : 'n/a';
                const schema = ev.schemaPosition
                  ? `(${Math.round(ev.schemaPosition.x)},${Math.round(ev.schemaPosition.y)})`
                  : 'n/a';
                return (
                  <div key={i} style={{ display: 'flex', gap: '6px', color: '#dfe7ff' }}>
                    <span style={{ color: '#f4d35e', minWidth: '110px' }}>{ev.phase}</span>
                    <span style={{ color: '#9db0d7', minWidth: '55px' }}>+{ev.elapsed}ms</span>
                    <span>s:{schema}</span>
                    <span>l:{local}</span>
                    <span style={{ color: ev.pendingInputs > 10 ? '#e74c3c' : '#9db0d7' }}>
                      p:{ev.pendingInputs}
                    </span>
                    <span style={{ color: '#9db0d7' }}>seq:{ev.currentSeq}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={sectionStyle}>
    <div style={sectionTitleStyle}>{title}</div>
    {children}
  </section>
);

const KV: React.FC<{ label: string; value: string; dataTestId?: string }> = ({ label, value, dataTestId }) => (
  <div style={kvRowStyle} data-testid={dataTestId}>
    <span style={kvLabelStyle}>{label}</span>
    <span style={kvValueStyle}>{value}</span>
  </div>
);

const metaRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
  color: '#9db0d7',
  marginBottom: '12px',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '14px',
  padding: '10px',
  border: '1px solid rgba(157, 176, 215, 0.2)',
  background: 'rgba(14, 22, 38, 0.75)',
  borderRadius: '6px',
};

const sectionTitleStyle: React.CSSProperties = {
  marginBottom: '8px',
  color: '#f4d35e',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const kvRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '4px',
};

const kvLabelStyle: React.CSSProperties = {
  color: '#9db0d7',
  flexShrink: 0,
};

const kvValueStyle: React.CSSProperties = {
  color: '#ffffff',
  textAlign: 'right',
  wordBreak: 'break-all',
};

const remoteCardStyle: React.CSSProperties = {
  padding: '8px',
  borderRadius: '6px',
  background: 'rgba(28, 40, 67, 0.78)',
  border: '1px solid rgba(157, 176, 215, 0.18)',
  marginTop: '8px',
};

const remoteHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
  color: '#f7f9ff',
};

const emptyStateStyle: React.CSSProperties = {
  color: '#9db0d7',
};

const healthBannerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 10px',
  marginBottom: '12px',
  border: '1px solid',
  borderRadius: '6px',
  background: 'rgba(14, 22, 38, 0.75)',
};


